import { ClickEvent, ContextMenuCreationEvent, ContextMenuLabelProps, DataSourceRemoteDataChangeEvent, ModelDataChangeType, Table, TableRow, TableSelectionEvent } from "@mcleod/components";
import { CommonContextMenuItems, getDispatchControlValue } from "@mcleod/general";
import { AutogenLayoutOrderPlanningToolPanel } from "./autogen/AutogenLayoutOrderPlanningToolPanel";
import { OrderTabView } from "../OrderTabView";
import { OrderPlanningSplitView } from "./opsmgmt/OrderPlanningSplitView";
import { RowDispatchControlFields } from "@mcleod/general/src/models/autogen/AutogenModelDispatchControl";
import { OrderPlanningRowAction, RowActionDispatch, RowActionDriverEvent, RowActionEquipManageAssignments, RowActionFeasibility, RowActionFindNear, RowActionHold, RowActionLink, RowActionLoadMatch, RowActionOrdersManageAssignments, RowActionSeatDriver, RowActionSeatTractor, RowActionSendMessage, RowActionTonu, RowActionUnassign, RowActionUpdateFields, RowActionViewMovements, RowActionYSplit, RowActionZmit } from "./calltoactions";
import { SelectionValidation } from "@mcleod/general/src/planning/PlanningTableRowAction";
import { ActionPermission, Alignment, HorizontalAlignment, LogManager, ModelRow, PermissionsUtil } from "@mcleod/core";
import { OrderPlanningActions } from "./OrderPlanningActions";
import { RowActionCancelSentLoad } from "./calltoactions/RowActionCancelSentLoad";

// Note: Please enter new tools in alphabetical order
export const Tools = {
    DISPATCH: RowActionDispatch,
    DRIVER_EVENT: RowActionDriverEvent,
    EQUIP_MANAGE_ASSIGN: RowActionEquipManageAssignments,
    FEASIBILITY: RowActionFeasibility,
    FINDNEAR: RowActionFindNear,
    HOLD: RowActionHold,
    LINK: RowActionLink,
    LOAD_MATCH: RowActionLoadMatch,
    ORDERS_MANAGE_ASSIGN: RowActionOrdersManageAssignments,
    SEAT_DRIVER: RowActionSeatDriver,
    SEAT_TRACTOR: RowActionSeatTractor,
    SEND_MESSAGE: RowActionSendMessage,
    TONU: RowActionTonu,
    UNASSIGN: RowActionUnassign,
    UPDATE_FIELDS: RowActionUpdateFields,
    VIEW_MOVEMENTS: RowActionViewMovements,
    YSPLIT: RowActionYSplit,
    ZMIT: RowActionZmit,
    CANCEL_SENT_LOAD: RowActionCancelSentLoad,
};

export type ToolConstructor = typeof Tools[keyof typeof Tools];

const log = LogManager.getLogger("lme.dispatch.planning.OrderPlanningToolPanel");

export class OrderPlanningToolPanel extends AutogenLayoutOrderPlanningToolPanel {
    private SINGLE_SELECTION_TEXT = "Only one order can be selected for this action";
    private MAX_SELECTION_TEXT = "Max rows exceeded for this action";
    private NO_SELECTION_TEXT = "No rows selected";
    private MULTI_PERMISSIONS_TEXT = "You do not have the permissions to perform this action on multiple rows."

    public splitViewLayout: OrderPlanningSplitView;
    public actionsLayout: OrderPlanningActions;
    public table: Table;
    private visibleActions: OrderPlanningRowAction[] = [];
    private unmodifiedVisibleActions: OrderPlanningRowAction[] = [];

    showButtons(actionsLayout: OrderPlanningActions, ...tools: ToolConstructor[]) {
        log.debug("Showing tools:", tools.map(t => t.name));
        this.actionsLayout = actionsLayout;
        this.table = actionsLayout.owner?.getTable();

        this.visibleActions = [];
        this.unmodifiedVisibleActions = [];
        for (const Tool of tools) {
            if (Tool.canShowAction()) {
                const action = new Tool(this);
                if (!PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/planning/OrderPlanningToolPanel",
                    action.getButtonId())) {
                    this.visibleActions.push(action);
                    this.unmodifiedVisibleActions.push(action);
                }
            }
        }
        log.debug("Visible actions:", this.visibleActions);

        if (this.visibleActions.length == 0) {
            this.removeAll();
            this.actionsLayout.buttonActions.disabledTooltip = "No actions available";
            this.actionsLayout.buttonActions.enabled = false;
            return;
        }

        this.table?.addSelectionListener((event: TableSelectionEvent) => {
            this.tableOnSelect(event);
        });
        this.table?.dataSource?.addRemoteDataChangeListener(
            (event: DataSourceRemoteDataChangeEvent) => this.onRemoteDataChange(event)
        );

        this.enableCtasButton(null, false);
    }

    prepareForOverlay(): boolean {
        if (this.visibleActions.length > 0) {
            this.filterVisibleActions();
            this.syncToolPanel();
            this.visibleActions[0].getButton().focus();
            this.visibleActions.forEach(action => {
                action.getButton().setProps({align: HorizontalAlignment.LEFT, tooltipPosition: Alignment.RIGHT});
            });
            return true;
        }
        return false;
    }

    public getUpdateFields(): string[] {
        let result = [];
        this.visibleActions.forEach(action => {
            result = [...new Set([...result, ...action.getFieldsUsedInThisFile()])];
        })

        return result;
    }

    setSplitViewLayout(layout: OrderPlanningSplitView) {
        this.splitViewLayout = layout;
    }

    onRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        if (event.change.type === ModelDataChangeType.DELETE) {
            const movementId = event.change.data.get("movement_id");
            if (movementId != null && this.table.selectedRow != null) {
                const selectedMoveId = this.table.selectedRow.data.get("movement_id");
                if (movementId === selectedMoveId)
                    this.table.selectedRow = null;
            }
        } else {
            const selectedRows = this.table.selectedRows.map(row => row.data as ModelRow);
            let selectedRowChanged = false;
            for (let i = 0; i < selectedRows.length; i++) {
                const modelRow = selectedRows[i];
                if (this.table.dataSource.compareRows(modelRow, event.change.data)) {
                    this.testUpdateFreshness(event, modelRow);
                    if (!event.defaultPrevented)
                        selectedRows[i] = event.change.data;
                    selectedRowChanged = true;
                    break;
                }
            }
            if (!event.defaultPrevented && selectedRowChanged)
                this.syncToolPanel(selectedRows);
        }
    }

    testUpdateFreshness(event: DataSourceRemoteDataChangeEvent, currentRow: ModelRow) {
        const changedRow = event.change.data as ModelRow;
        if (currentRow != null && !currentRow.isNull("lastUpdateNanos") &&
            currentRow.get("lastUpdateNanos") > changedRow.get("lastUpdateNanos")) {
            log.debug("Data change dropped due to stale data", event);
            event.preventDefault();
        }
        else {
            log.debug("Will handle data change:", event);
        }
    }

    public getVisibleAction<T extends OrderPlanningRowAction>(tool: ToolConstructor): T {
        for (const action of this.visibleActions) {
            if (action instanceof tool) {
                log.debug("Found action:", action)
                return action as unknown as T;
            }
        }
        return null;
    }

    private noRowsSelected(modelRows: ModelRow[]): boolean {
        return modelRows == null || modelRows.length < 1
    }

    private exceedsMaxSelectedRows(fieldOrMax: keyof RowDispatchControlFields | number, selectionCount: number): boolean {
        let max: number;
        if (typeof fieldOrMax === "number") {
            max = fieldOrMax;
        } else {
            max = getDispatchControlValue(fieldOrMax) as number;
        }
        max ??= 25;
        return selectionCount > max ;
    }

    private isMultiSelectAllowed(action: OrderPlanningRowAction): boolean {
        const actionPermission: ActionPermission | null = action.getMultiSelectPermission();
        return actionPermission ? !PermissionsUtil.isUserDeniedAction(actionPermission) : true;
    }

    public isForOrderMoves(): boolean {
        return this.table.id === "tableAvailableMoves";
    }

    public isForEquipMoves(): boolean {
        return this.table.id === "tableEquipMoves" || this.table.id === "tableDriverManagement" || this.table.id === "tableTimeline";
    }

    get findNearAction() {
        const action = this.visibleActions.find(a => a instanceof RowActionFindNear);
        if (action) {
            return action as RowActionFindNear;
        } else if (this.table.id !== "tableAvailableMoves") {
            return this.splitViewLayout?.layoutOrderPlanningOrders?.getToolsPanel().findNearAction;
        }
    }

    findNearOn(): boolean {
        const findNearAction = this.findNearAction;
        return findNearAction ? findNearAction.findNearOn : false;
    }

    async tableOnSelect(event: TableSelectionEvent) {
        // if find near is on, selection mode will be single
        if (this.findNearOn() && event.table.id === "tableAvailableMoves" &&
                event.newRow?.data?.get("order_id") !== event.oldRow?.data?.get("order_id")) {
            await this.findNearAction.applyFindNear(event.newRow != null);
        }
        this.enableCtasButton(event);
        this.updateConditionalVisibility(event.newRow?.data as ModelRow);
    }

    public updateConditionalVisibility(modelRow: ModelRow) {
        for (const action of this.unmodifiedVisibleActions) {
            if (action.isConditionallyVisible()) {
                const visible = action.checkVisibility(modelRow);
                action.getButton().visible = visible;
                action.getButton().enabled = visible;
            }
        }
    }


    public syncToolPanel(modelRows?: ModelRow[]) {
        this.enableCtasButton();
        if (!this.actionsLayout.buttonActions.enabled) return;
        modelRows ??= this.table.selectedRows.map(row => row.data as ModelRow);
        for (const action of this.visibleActions) {
            this.syncButtonState(action, modelRows);
        }
    }

    public syncButtonState(action: OrderPlanningRowAction, modelRows: ModelRow[]) {
        const button = action.getButton();
        const noRows = this.noRowsSelected(modelRows);
        button.disabledTooltip = null;
        button.enabled = true;

        if (action.isConditionallyVisible() && !button.visible) {
            button.enabled = false;
            return;
        }

        const maxSelections = action.getMaxSelections();
        if (noRows) {
            button.disabledTooltip = this.NO_SELECTION_TEXT;
            button.enabled = false;
            if (!action.deselectionAllowed())
                return;
        } else if (this.exceedsMaxSelectedRows(maxSelections, modelRows.length)) {
            button.disabledTooltip = (typeof maxSelections === "number" && maxSelections == 1)
                ? this.SINGLE_SELECTION_TEXT : this.MAX_SELECTION_TEXT;
            button.enabled = false;
            return;
        } else if (modelRows.length > 1 && !this.isMultiSelectAllowed(action)) {
            button.disabledTooltip = this.MULTI_PERMISSIONS_TEXT;
            button.enabled = false;
            return;
        }

        if (button.enabled || action.deselectionAllowed()) {
            const res: SelectionValidation | Promise<SelectionValidation> = action.validateSelection(modelRows);
            if (res instanceof Promise) {
                // Prevent the action from being clicked while we validate
                button.enabled = false;
                log.debug(`Adding ${action.constructor.name} to asyncActions`)
                const completionPromise = res.then(result => {
                    // Only apply the result if this promise is still the latest one triggered
                    if (action.validationPromise !== completionPromise) {
                        log.debug(`Outdated validation result for ${action.constructor.name}, ignoring`);
                        return;
                    }
                    this.handleSyncResults(action, result);
                }).catch(error => {
                    log.error(`Validation error for ${action.constructor.name}:`, error);
                    if (action.validationPromise === completionPromise) {
                        button.enabled = false;
                        button.disabledTooltip = "Validation error occurred";
                    }
                });
                action.validationPromise = completionPromise;
            } else {
                this.handleSyncResults(action, res);
            }
        }
    }

    private enableCtasButton(event?: TableSelectionEvent, override?: boolean) {
        if (!this.actionsLayout.buttonActions) return;
        const selectedRows = event ? event.newRows : this.table.selectedRows;
        const enabled = override ?? selectedRows.length > 0;
        this.actionsLayout.buttonActions.enabled = enabled;
        if (!enabled) this.actionsLayout.buttonActions.disabledTooltip = this.NO_SELECTION_TEXT;
    }

    private handleSyncResults(action: OrderPlanningRowAction, selectionValidation: SelectionValidation) {
        action.getButton().enabled = selectionValidation.enabled;
        action.getButton().disabledTooltip = selectionValidation.enabled ? null : action.buildDisabledReason(selectionValidation.reasons);
    }

    private filterVisibleActions() {
        this.visibleActions = this.unmodifiedVisibleActions.filter(action => {
            if (action.isConditionallyVisible()) {
                return action.getButton().visible;
            }
            return true;
        });
    }

    public getContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        if (tableRow) {
            if (!tableRow.table.selectedRows.includes(tableRow)){
                tableRow.table.selectedRows = [tableRow];
            } else if (tableRow.table.selectedRows.length <= 1) {
                tableRow.table.selectedRow = tableRow;
            }
        }
        this.syncToolPanel();

        const rightClickItems: Partial<ContextMenuLabelProps>[] = [];
        this.filterVisibleActions();
        const fromButtons = this.visibleActions.map(action => this.customContextMenuItemCreator(action));

        if (tableRow.table.selectedRows.length > 1) {
            return [
                ...fromButtons,
                ...rightClickItems
            ]
        }

        const order_id = tableRow?.data?.get("order_id", "null");
        const movement_id = tableRow?.data?.get("movement_id");

        return OrderTabView.addNavigationContextMenuItems(
            [
                ...fromButtons,
                ...rightClickItems,
                CommonContextMenuItems.createRecentChangesItemFromTableRow(tableRow ?? this.table.selectedRows[0]),
            ],
            order_id,
            movement_id
        )
    }

    contextMenuCreationEvent(event: ContextMenuCreationEvent) {
        for (const label of event.menuLabels) {
            log.debug("Context Menu Label:", label);
            const action = this.visibleActions.find((action) =>
                action.getButton().displayLabel === label.displayLabel);
            if (action) {
                if (action.isConditionallyVisible()) {
                    label.visible = action.getButton().visible;
                }
                log.debug("Menu Item Label, Action:", label, action)
                if (action.validationPromise != null) {
                    log.debug(`Matched on action ${action.constructor.name} in asyncActions `);
                    const currentPromise = action.validationPromise;
                    currentPromise.then(_ => {
                        label.enabled = action.getButton().enabled
                        label.disabledTooltip = action.getButton().disabledTooltip
                        // Only clear the promise if another validation hasn't started
                        if (action.validationPromise === currentPromise) {
                            action.validationPromise = null;
                        }
                    });
                } else {
                    label.visible = action.getButton().enabled;
                }
            }
        }
    }

    customContextMenuItemCreator(action: OrderPlanningRowAction): Partial<ContextMenuLabelProps> {
        const button = action.getButton();
        return {
            text: button.caption ?? button.displayLabel,
            displayLabel: button.displayLabel,
            onClick: ((event: ClickEvent) => action.execute(action.getExecuteProps(event))),
            visible: button.parent != null && button.enabled,
            imageName: button.imageName,
            imageRotation: button.imageRotation,
            imageProps: button.imageProps,
        };
    }
}
